'use client'

import { useState, useContext } from 'react'
import { RefreshCw, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AuthContext } from '@/contexts/AuthContext'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import { fetcher } from '@/lib/api/fetch'

/**
 * A single admin action row: title + description on the left, action button on the right.
 * Designed to stack vertically as more actions are added.
 */
function ActionRow({ title, description, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0 border-b border-border last:border-0">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

export default function AdminActions() {
  const authContext = useContext(AuthContext)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshResult, setRefreshResult] = useState(null)
  const [error, setError] = useState(null)

  const handleRefreshPreparedness = async () => {
    setRefreshing(true)
    setError(null)
    setRefreshResult(null)

    try {
      const orgId = authContext.user?.organization?.id
      const res = await fetcher(
        `/api/admin/organization/${orgId}/reports/readiness/refresh`,
        { method: 'POST' },
        authContext,
      )

      const data = await res.json()
      if (!res.ok) {
        setError(data?.error || 'Failed to refresh preparedness data')
        return
      }

      setRefreshResult(data.written)
      setTimeout(() => setRefreshResult(null), 6000)
    } catch {
      setError('Failed to refresh preparedness data')
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="grid gap-4">
        <div>
          <h6 className="text-base font-semibold">Admin actions</h6>
          <p className="text-sm text-muted-foreground">
            Perform one-off administrative tasks for your organisation.
          </p>
        </div>

        <ErrorAlerts error={error} />

        {refreshResult !== null && (
          <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
            <CheckCircle2 className="size-4" />
            <AlertDescription>
              Refreshed {refreshResult}{' '}
              {refreshResult === 1 ? 'entry' : 'entries'}.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col">
          <ActionRow
            title="Refresh preparedness data"
            description="Manually recompute readiness scores. Normally runs automatically at 02:00 UTC daily."
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshPreparedness}
              disabled={refreshing}
            >
              <RefreshCw
                className={refreshing ? 'size-4 animate-spin' : 'size-4'}
              />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </ActionRow>
        </div>
      </div>
    </div>
  )
}
